'use client';

import { Editor } from '@monaco-editor/react';
import { Copy, Code2, Sparkles, Zap } from 'lucide-react';
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CodeSnippet, codeSnippets } from '@/data/code-snippets';
import Image from 'next/image';

const CodeShowcase: React.FC = () => {
  const [selectedSnippet, setSelectedSnippet] = useState<CodeSnippet>(
    codeSnippets[0],
  );
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check for mobile view
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(selectedSnippet.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSnippetSelect = (snippet: CodeSnippet) => {
    setSelectedSnippet(snippet);
  };

  // Get responsive editor height
  const getEditorHeight = () => {
    if (isMobile) return '400px';
    return '600px';
  };

  // Get responsive editor options
  const getEditorOptions = () => ({
    readOnly: true,
    minimap: { enabled: !isMobile },
    scrollBeyondLastLine: false,
    fontSize: isMobile ? 12 : 14,
    lineNumbers: isMobile ? ('off' as const) : ('on' as const),
    glyphMargin: false,
    folding: !isMobile,
    lineDecorationsWidth: 0,
    lineNumbersMinChars: isMobile ? 0 : 3,
    renderLineHighlight: 'none' as const,
    wordWrap: isMobile ? ('on' as const) : ('off' as const),
    wordWrapColumn: isMobile ? 40 : 120,
    wordWrapMinified: true,
    wrappingIndent: 'indent' as const,
    wrappingStrategy: 'advanced' as const,
    scrollbar: {
      verticalScrollbarSize: isMobile ? 6 : 8,
      horizontalScrollbarSize: isMobile ? 4 : 8,
      horizontal: isMobile ? ('hidden' as const) : ('auto' as const),
      vertical: 'auto' as const,
    },
    automaticLayout: true,
    overviewRulerLanes: isMobile ? 0 : 3,
    hideCursorInOverviewRuler: true,
    padding: {
      top: isMobile ? 8 : 16,
      bottom: isMobile ? 8 : 16,
    },
    quickSuggestions: false,
    parameterHints: { enabled: false },
    suggestOnTriggerCharacters: false,
    acceptSuggestionOnEnter: 'off' as const,
    tabCompletion: 'off' as const,
    wordBasedSuggestions: 'off' as const,
    smoothScrolling: true,
    cursorSmoothCaretAnimation: isMobile ? ('off' as const) : ('on' as const),
    contextmenu: !isMobile,
    mouseWheelZoom: !isMobile,
  });

  return (
    <section className="from-background via-background to-card/0 relative mb-8 min-h-screen overflow-hidden bg-gradient-to-br">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="bg-primary/5 absolute -top-40 -right-40 h-80 w-80 rounded-full blur-3xl" />
        <div className="bg-secondary/0 absolute -bottom-40 -left-40 h-80 w-80 rounded-full blur-3xl" />
        <div className="bg-accent/5 absolute top-1/2 right-3/4 h-220 w-220 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 py-20 md:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16 text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-primary/10 border-primary/20 mb-6 inline-flex items-center gap-3 rounded-full border px-6 py-3"
          >
            <Code2 className="h-5 w-5 text-blue-500" />
            <span className="text-sm font-medium text-blue-400">
              Code Showcase
            </span>
          </motion.div>

          <div className="from-foreground via-primary to-foreground mb-6 bg-gradient-to-r bg-clip-text text-4xl font-bold text-transparent md:text-5xl lg:text-6xl xl:text-7xl">
            <span className="text-foreground">Crafting</span> Digital Excellence
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="text-foreground antonio mx-auto max-w-2xl font-serif text-lg leading-relaxed md:max-w-4xl md:text-xl lg:text-2xl"
          >
            Explore the code behind my projects, showcasing clean architecture,
            modern patterns, and elegant solutions that bring ideas to life.
          </motion.p>
        </motion.div>

        {/* Code Showcase */}
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            {/* Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="space-y-4 lg:col-span-4"
            >
              {/* Mobile Navigation */}
              <div className="lg:hidden">
                <div className="bg-card/50 border-border/50 mb-6 flex items-center justify-between gap-4 rounded-2xl border p-4 backdrop-blur-sm">
                  <button
                    onClick={() => {
                      const currentIndex = codeSnippets.findIndex(
                        s => s.id === selectedSnippet.id,
                      );
                      const prevIndex =
                        currentIndex > 0
                          ? currentIndex - 1
                          : codeSnippets.length - 1;
                      setSelectedSnippet(codeSnippets[prevIndex]);
                    }}
                    aria-label="View previous code snippet"
                    className="bg-primary/20 hover:bg-primary/30 flex h-10 w-10 items-center justify-center rounded-xl text-blue-500 transition-all hover:scale-105"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  <div className="flex gap-2">
                    {codeSnippets.map(snippet => (
                      <button
                        key={snippet.id}
                        onClick={() => setSelectedSnippet(snippet)}
                        aria-label={`View ${snippet.title} code snippet`}
                        aria-current={
                          selectedSnippet.id === snippet.id ? 'true' : 'false'
                        }
                        className={`flex h-9 w-9 items-center justify-center rounded-xl p-1 transition-all duration-200 ${
                          selectedSnippet.id === snippet.id
                            ? 'bg-primary text-primary-foreground shadow-primary/25 scale-110 shadow-lg'
                            : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                      >
                        <Image
                          src={snippet.icon}
                          alt={snippet.iconAlt}
                          width={32}
                          height={32}
                          className="h-6 w-6 object-contain brightness-0 invert saturate-0"
                        />
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => {
                      const currentIndex = codeSnippets.findIndex(
                        s => s.id === selectedSnippet.id,
                      );
                      const nextIndex =
                        currentIndex < codeSnippets.length - 1
                          ? currentIndex + 1
                          : 0;
                      setSelectedSnippet(codeSnippets[nextIndex]);
                    }}
                    aria-label="View next code snippet"
                    className="bg-primary/20 hover:bg-primary/30 flex h-10 w-10 items-center justify-center rounded-xl text-blue-500 transition-all hover:scale-105"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>

                {/* Mobile Project Info */}
                <div className="hidden lg:block bg-card/50 border-border/50 mb-6 rounded-2xl border p-6 backdrop-blur-sm">
                  <div className="text-foreground mb-3 text-xl font-semibold">
                    {selectedSnippet.title}
                  </div>
                  <p className="text-muted-foreground mb-4">
                    {selectedSnippet.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedSnippet.tags.map(tag => (
                      <span
                        key={tag}
                        className="bg-primary/20 border-primary/30 rounded-full border px-3 py-1 text-xs font-medium text-blue-500"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Desktop Sidebar */}
              <div className="hidden space-y-4 lg:block">
                {codeSnippets.map((snippet, index) => (
                  <motion.div
                    key={snippet.id}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                    onClick={() => handleSnippetSelect(snippet)}
                    className={`group cursor-pointer rounded-2xl border-2 p-6 transition-all duration-300 hover:scale-[1.02] ${
                      selectedSnippet.id === snippet.id
                        ? 'border-primary/50 bg-primary/10 shadow-primary/20 shadow-xl'
                        : 'border-border/50 bg-card/30 hover:border-primary/30 hover:bg-primary/5'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div
                          className={`flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-300 ${
                            selectedSnippet.id === snippet.id
                              ? 'bg-primary shadow-primary/25 text-blue-500 shadow-lg'
                              : 'bg-muted/50 text-muted-foreground group-hover:bg-primary/20 group-hover:text-blue-500'
                          }`}
                        >
                          <Image
                            src={snippet.icon}
                            alt={snippet.iconAlt}
                            width={48}
                            height={48}
                            className="h-8 w-8 object-contain brightness-0 invert saturate-0"
                          />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div
                          className={`mb-2 font-semibold transition-colors duration-300 ${
                            selectedSnippet.id === snippet.id
                              ? 'text-blue-500'
                              : 'text-foreground group-hover:text-blue-500'
                          }`}
                        >
                          {snippet.title}
                        </div>
                        <p className="text-muted-foreground mb-3 line-clamp-3 text-sm">
                          {snippet.description}
                        </p>
                        <div className="text-muted-foreground flex items-center gap-2 text-xs">
                          <span className="flex items-center gap-1">
                            <Code2 className="h-3 w-3" />
                            {snippet.language}
                          </span>
                          <span className="flex items-center gap-1">
                            <Sparkles className="h-3 w-3" />
                            {snippet.tags.length} features
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Code Display */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="lg:col-span-8"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedSnippet.id}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.95 }}
                  transition={{ duration: 0.4 }}
                  className="group border-border/50 bg-card/50 relative overflow-hidden rounded-2xl border shadow-2xl backdrop-blur-sm"
                >
                  {/* Glowing Border Effect */}
                  <div className="from-primary/20 to-secondary/20 absolute inset-0 rounded-2xl bg-gradient-to-r via-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                  {/* Header */}
                  <div className="relative p-6">
                    <div className="mb-6 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="bg-primary/20 text-primary flex h-10 w-10 items-center justify-center rounded-xl">
                          <Image
                            src={selectedSnippet.icon}
                            alt={selectedSnippet.iconAlt}
                            width={32}
                            height={32}
                            className="h-6 w-6 object-contain brightness-0 invert saturate-0"
                          />
                        </div>
                        <div>
                          <h2 className="text-foreground text-xl font-semibold md:text-2xl">
                            {selectedSnippet.title}
                          </h2>
                          <p className="text-muted-foreground text-sm">
                            {selectedSnippet.language} •{' '}
                            {selectedSnippet.tags.length} features
                          </p>
                        </div>
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleCopy}
                        className="bg-primary/20 hover:bg-primary/30 hover:shadow-primary/25 flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-blue-500 transition-all hover:shadow-lg"
                      >
                        <Copy size={16} />
                        <span className="hidden sm:inline">
                          {copied ? 'Copied!' : 'Copy'}
                        </span>
                      </motion.button>
                    </div>

                    <div className="space-y-4">
                      <p className="text-muted-foreground leading-relaxed">
                        {selectedSnippet.description}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {selectedSnippet.tags.map((tag, index) => (
                          <motion.span
                            key={tag}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                            className="bg-primary/20 border-primary/30 rounded-full border px-3 py-1 text-xs font-medium text-blue-500"
                          >
                            {tag}
                          </motion.span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Code Editor Container */}
                  <div className="border-border/30 bg-background/50 relative mx-6 mb-6 overflow-hidden rounded-xl border">
                    {/* Editor Header */}
                    <div className="border-border/30 bg-muted/30 flex items-center justify-between border-b px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <div className="h-3 w-3 rounded-full bg-red-500" />
                          <div className="h-3 w-3 rounded-full bg-yellow-500" />
                          <div className="h-3 w-3 rounded-full bg-green-500" />
                        </div>
                        <span className="text-muted-foreground ml-2 font-mono text-xs">
                          {selectedSnippet.title}.{selectedSnippet.language}
                        </span>
                      </div>
                      <div className="text-muted-foreground flex items-center gap-2 text-xs">
                        <Zap className="h-3 w-3" />
                        <span>Ready</span>
                      </div>
                    </div>

                    {/* Monaco Editor */}
                    <div
                      className="monaco-editor-container"
                      style={{ height: getEditorHeight() }}
                    >
                      <Editor
                        height="100%"
                        language={selectedSnippet.language}
                        value={selectedSnippet.code}
                        theme="vs-dark"
                        options={getEditorOptions()}
                        loading={
                          <div className="flex h-full items-center justify-center">
                            <div className="text-muted-foreground flex items-center gap-2">
                              <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                              <span>Loading editor...</span>
                            </div>
                          </div>
                        }
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="border-border/30 bg-muted/20 border-t px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="text-muted-foreground flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1">
                          <Code2 className="h-4 w-4" />
                          {selectedSnippet.language}
                        </span>
                        <span className="flex items-center gap-1">
                          <Sparkles className="h-4 w-4" />
                          {selectedSnippet.tags.length} features
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CodeShowcase;
